import { READONLY_APP_ROLES, type AppRole } from "@/hooks/accessRoleUtils";
import type { OrgRole } from "@/hooks/useAccessContext";

/**
 * Who may see and change scan-to-email intake (`ap_inbound_addresses`, and its log
 * `ap_inbound_emails`, which carries the same policies), decided from the roles the app has already
 * loaded, the way Deposits mirrors its RLS roles (`DEPOSIT_ROLES`). Display gating only: RLS stays
 * the authority, and this never grants anything the server would refuse.
 *
 * Every policy on both tables goes through `can_access_finance_org(org_id, roles)`
 * (20260622150000_finance_org_scope_role_check.sql): the viewer must be a member of the
 * organization and hold one of the roles in it or globally. Those are `orgRole` and
 * `orgAppRolesDbOnly` from usePermissions; the helper reads public.user_roles, never the JWT, so
 * metadata-only roles don't count. The read-only block on writes is wider for one role (see
 * `canEditScanToEmail`), which is why `appRolesDbOnly` is here too.
 */
export interface ScanToEmailViewer {
  /** The organization_members role in the active organization; null when not a member. */
  orgRole: OrgRole | null | undefined;
  /** user_roles rows in the active organization plus global (null-org) ones. */
  orgAppRolesDbOnly: readonly AppRole[] | null | undefined;
  /** Every user_roles row the viewer holds, in any organization (usePermissions' `appRolesDbOnly`). */
  appRolesDbOnly: readonly AppRole[] | null | undefined;
}

/**
 * The INSERT, UPDATE and DELETE policies (setting up the address, and every save), in
 * 20260615210000_rls_normalize_multiple_permissive.sql; no later migration redefines them.
 */
export const SCAN_TO_EMAIL_WRITE_ROLES: readonly AppRole[] = [
  "finance_admin",
  "bookkeeper",
  "ap_clerk",
  "finance",
  "admin",
  "super_admin",
];

/**
 * The SELECT policy: the write roles, then `can_access_finance_org(org_id)`'s default list (admin,
 * super_admin and the three platform roles), in 20260615210000_rls_normalize_multiple_permissive.sql;
 * and `auditor`, whose read 20260626210000_finance_rbac_complete_audit_personas.sql adds to every
 * table guarded by `can_access_finance_org` (20260630030000_rls_normalize_recent_policy_overlays.sql
 * folds it into the same policy).
 */
export const SCAN_TO_EMAIL_READ_ROLES: readonly AppRole[] = [
  ...SCAN_TO_EMAIL_WRITE_ROLES,
  "platform_super_admin",
  "platform_support",
  "platform_readonly",
  "auditor",
];

/**
 * The read-only role `is_readonly_app_user_for_org` matches whichever organization its row names
 * (20260803222239_readonly_write_authority.sql, last redefined in
 * 20260921230000_tombstone_write_gate_starts_when_deletion_starts.sql). Its other read-only role,
 * auditor, counts only for this organization or globally.
 */
const READONLY_IN_EVERY_ORG: readonly AppRole[] = ["platform_readonly"];

function holdsAny(held: readonly AppRole[] | null | undefined, roles: readonly AppRole[]): boolean {
  return (held ?? []).some((role) => roles.includes(role));
}

/**
 * Whether RLS lets the viewer read this organization's scan-to-email settings and log. A refused
 * read returns no row, not an error, which the page would otherwise show as "no address yet".
 */
export function canReadScanToEmail(viewer: ScanToEmailViewer): boolean {
  return Boolean(viewer.orgRole) && holdsAny(viewer.orgAppRolesDbOnly, SCAN_TO_EMAIL_READ_ROLES);
}

/**
 * Whether RLS lets the viewer set up and change them: a write role, and no read-only role. The
 * restrictive readonly_roles_block_* policies refuse any write by an auditor of this organization
 * (or a global one), or by a platform_readonly user, whatever else they hold. platform_readonly
 * counts from every organization, so a finance writer here with a platform_readonly row for
 * another organization is still read-only here.
 */
export function canEditScanToEmail(viewer: ScanToEmailViewer): boolean {
  return (
    canReadScanToEmail(viewer)
    && holdsAny(viewer.orgAppRolesDbOnly, SCAN_TO_EMAIL_WRITE_ROLES)
    && !holdsAny(viewer.orgAppRolesDbOnly, READONLY_APP_ROLES)
    && !holdsAny(viewer.appRolesDbOnly, READONLY_IN_EVERY_ORG)
  );
}
